import { useCallback, useEffect, useState } from 'react'
import {
  errorMessage,
  exportSmsUsageCsv,
  getSmsStatementEmailStatus,
  getSmsUsageStatement,
  listSmsUsageMonths,
  sendSmsStatementEmail,
} from '../../services/admin'
import { confirmed } from './confirm'
import { emailOutcomeText, emailStatusText, groupedFa, smsCsvFileName, smsMonthLabel, smsPurposeLabel } from './smsUsage'

// ─── SMS usage tab «مصرف پیامک» (tooti#316) ───────────────────────────────────

const selectCls = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

function Card({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{value}</div>
      {hint && <div className="text-xs text-slate-400">{hint}</div>}
    </div>
  )
}

function UsageTable({ provider, showCost }) {
  if (provider.lines.length === 0) {
    return <p className="rounded-xl border border-slate-200 bg-white py-6 text-center text-sm text-slate-400">در این ماه پیامکی ثبت نشده.</p>
  }
  const row = (key, label, l, strong) => (
    <tr key={key} className={`border-t border-slate-100 ${strong ? 'bg-slate-50 font-semibold' : ''}`}>
      <td className="px-4 py-2 text-slate-700">{label}</td>
      <td className="px-4 py-2 text-slate-700">{groupedFa(l.sends)}</td>
      <td className="px-4 py-2 text-slate-700">{groupedFa(l.parts)}</td>
      {showCost && <td className="px-4 py-2 text-slate-700">{groupedFa(l.costUnits)}</td>}
      <td className="px-4 py-2 text-emerald-700">{groupedFa(l.delivered)}</td>
      <td className="px-4 py-2 text-amber-700">{groupedFa(l.undelivered)}</td>
      <td className="px-4 py-2 text-rose-700">{groupedFa(l.failed)}</td>
    </tr>
  )
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-xs text-slate-500">
            <th className="px-4 py-2 text-start">کاربرد</th>
            <th className="px-4 py-2 text-start">ارسال</th>
            <th className="px-4 py-2 text-start">بخش پیامک</th>
            {showCost && <th className="px-4 py-2 text-start">واحد هزینه</th>}
            <th className="px-4 py-2 text-start">تحویل‌شده</th>
            <th className="px-4 py-2 text-start">تحویل‌نشده</th>
            <th className="px-4 py-2 text-start">ناموفق</th>
          </tr>
        </thead>
        <tbody>
          {provider.lines.map(l => row(l.purpose, smsPurposeLabel(l.purpose), l, false))}
          {row('total', 'جمع', provider.total, true)}
        </tbody>
      </table>
    </div>
  )
}

/**
 * One month's statement, given `admin-sms-usage_statement`'s answer: the
 * SMS.ir share (what Rima bills) as cards and a table by purpose, then
 * Vonage separately for reference. Money is the wire's exact strings.
 */
export function SmsUsageView({ statement }) {
  const { smsIr, vonage, rialPerUnit } = statement
  const month = smsMonthLabel(statement.key, statement.calendar)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card label="پیامک‌های SMS.ir" value={groupedFa(smsIr.total.sends)} hint={`${groupedFa(smsIr.total.parts)} بخش`} />
        <Card label="واحد هزینهٔ SMS.ir" value={groupedFa(smsIr.total.costUnits)} hint="جمع هزینهٔ گزارش‌شده توسط SMS.ir" />
        <Card label="تعرفه (ریال به ازای هر واحد)" value={rialPerUnit == null ? '—' : groupedFa(rialPerUnit)} hint={rialPerUnit == null ? 'هنوز تعیین نشده؛ از تب «تنظیمات» وارد کنید' : undefined} />
        <Card
          label={`مبلغ ${month}`}
          value={(smsIr.totalAmountRial ?? smsIr.amountRial) == null ? '—' : `${groupedFa(smsIr.totalAmountRial ?? smsIr.amountRial)} ریال`}
          hint={smsIr.amountRial == null
            ? 'تا تعیین تعرفه فقط تعداد نمایش داده می‌شود'
            : smsIr.estimate
              ? `ثبت‌شده ${groupedFa(smsIr.amountRial)} + تخمینی ${groupedFa(smsIr.estimate.amountRial)} ریال`
              : 'واحد × تعرفه، گرد به نزدیک‌ترین ریال'} />
      </div>

      {smsIr.estimate && (
        <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <span className="font-semibold">تخمینی:</span> {groupedFa(smsIr.estimate.sends)} ارسال پیش از راه‌اندازی دفتر ثبت پیامک،
          {' '}{groupedFa(smsIr.estimate.parts)} بخش × ۱ واحد = {groupedFa(smsIr.estimate.units)} واحد
          {smsIr.estimate.amountRial != null && <> ← {groupedFa(smsIr.estimate.amountRial)} ریال</>}.
          {' '}SMS.ir هزینهٔ این ارسال‌ها را به ما نداده؛ جدا از مبلغ ثبت‌شده نشان داده می‌شود.
        </p>
      )}
      {smsIr.total.withoutCost - (smsIr.estimate?.sends ?? 0) > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {groupedFa(smsIr.total.withoutCost - (smsIr.estimate?.sends ?? 0))} ارسال بدون هزینهٔ ثبت‌شده است (ناموفق‌ها هزینه ندارند).
        </p>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">SMS.ir (خط مشترک ریما) — به تفکیک کاربرد</h3>
        <UsageTable provider={smsIr} showCost />
      </div>

      <div>
        <h3 className="mb-1 text-sm font-semibold text-slate-700">Vonage (شماره‌های خارج از ایران)</h3>
        <p className="mb-2 text-xs text-slate-400">
          جدا از صورت‌حساب SMS.ir، فقط برای اطلاع؛ هزینه روی حساب Vonage کسر می‌شود.
          {(vonage.amounts ?? []).length > 0
            ? <> هزینهٔ گزارش‌شده: <span dir="ltr" className="font-medium text-slate-600">{vonage.amounts.map(a => `${a.price} ${a.currency}`).join(' + ')}</span></>
            : ' هزینه‌ای از Vonage گزارش نشده.'}
        </p>
        <UsageTable provider={vonage} showCost={false} />
      </div>
    </div>
  )
}

/**
 * The finance-period email box: whether this period's statement went to the
 * finance mailbox, and «ارسال» / «ارسال دوباره» — both behind a confirm,
 * since an email cannot be taken back.
 */
export function StatementEmailBox({ status, result, sending, onSend }) {
  const sent = Boolean(status?.sentAt)
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold text-slate-700">ایمیل صورت‌حساب به واحد مالی</div>
        <div className="text-xs text-slate-500">
          {status ? emailStatusText(status) : '…'}
          {status?.to?.length > 0 && <> — گیرنده: <span dir="ltr">{status.to.join(', ')}</span></>}
        </div>
        {result && <div className="mt-1 text-xs text-brand-700">{emailOutcomeText(result)}</div>}
      </div>
      <button type="button" onClick={() => onSend(sent)} disabled={!status || sending}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
        {sending ? '…' : sent ? 'ارسال دوباره' : 'ارسال ایمیل'}
      </button>
    </div>
  )
}

/**
 * The «مصرف پیامک» tab (tooti#316): pick a calendar and a month, see the
 * statement, download it as CSV. Opens on the current Shamsi month.
 */
export function SmsUsageTab() {
  const [calendar, setCalendar] = useState('shamsi')
  const [months, setMonths] = useState([])
  const [month, setMonth] = useState('')
  const [statement, setStatement] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [err, setErr] = useState('')
  const [emailStatus, setEmailStatus] = useState(null)
  const [emailResult, setEmailResult] = useState(null)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    setEmailStatus(null); setEmailResult(null)
    if (calendar !== 'finance' || !month) return
    let live = true
    getSmsStatementEmailStatus(month).then(s => { if (live) setEmailStatus(s) }).catch(ex => { if (live) setErr(errorMessage(ex)) })
    return () => { live = false }
  }, [calendar, month])

  async function sendEmail(resend) {
    const label = smsMonthLabel(month, 'finance')
    const question = resend
      ? `ایمیل صورت‌حساب دورهٔ ${label} قبلاً فرستاده شده. دوباره برای واحد مالی فرستاده شود؟`
      : `ایمیل صورت‌حساب دورهٔ ${label} همین حالا برای واحد مالی فرستاده شود؟ پس‌گرفتنی نیست.`
    if (!confirmed(question)) return
    setSending(true); setErr(''); setEmailResult(null)
    try {
      setEmailResult(await sendSmsStatementEmail(month, { resend }))
      setEmailStatus(await getSmsStatementEmailStatus(month))
    } catch (ex) {
      setErr(errorMessage(ex))
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    let live = true
    setErr('')
    listSmsUsageMonths(calendar)
      .then(list => { if (live) { setMonths(list); setMonth(list[0] ?? '') } })
      .catch(ex => { if (live) setErr(errorMessage(ex)) })
    return () => { live = false }
  }, [calendar])

  const load = useCallback(async (key, cal) => {
    setLoading(true); setErr('')
    try { setStatement(await getSmsUsageStatement(key, cal)) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { if (month) load(month, calendar) }, [load, month, calendar])

  async function download() {
    setExporting(true); setErr('')
    try {
      const blob = await exportSmsUsageCsv(month, calendar)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = smsCsvFileName(month)
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (ex) {
      setErr(errorMessage(ex))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">تقویم</span>
          <select value={calendar} onChange={e => { setStatement(null); setCalendar(e.target.value) }} className={selectCls}>
            <option value="shamsi">شمسی</option>
            <option value="gregorian">میلادی</option>
            <option value="finance">دورهٔ مالی (۲۵ تا ۲۴)</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">ماه</span>
          <select value={month} onChange={e => setMonth(e.target.value)} className={`min-w-36 ${selectCls}`}>
            {months.map(k => <option key={k} value={k}>{smsMonthLabel(k, calendar)}</option>)}
          </select>
        </label>
        <button type="button" onClick={download} disabled={!month || exporting}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {exporting ? '…' : 'دریافت CSV'}
        </button>
        <span className="text-xs text-slate-400">ماه‌ها از نیمه‌شب تهران؛ مصرف طوطی از خط پیامک ریما</span>
      </div>

      {calendar === 'finance' && month && <StatementEmailBox status={emailStatus} result={emailResult} sending={sending} onSend={sendEmail} />}
      {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      {loading && !statement && <p className="py-10 text-center text-sm text-slate-400">در حال بارگذاری…</p>}
      {statement && <SmsUsageView statement={statement} />}
    </div>
  )
}
